import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from 'react';
import { WarningCircle, CircleNotch } from '@phosphor-icons/react';
import {Alert, AlertDescription} from './ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

export interface JustifiPaymentFormHandle {
  submit: () => Promise<void>;
}

export interface JustifiPaymentMethod {
  paymentMethodId: string;
  type: 'card' | 'bank_account';
  last4?: string;
  brand?: string;
  expMonth?: string;
  expYear?: string;
}

export interface JustifiBillingPrefill {
  name?: string;
  address_postal_code?: string;
}

/**
 * The slice of the `<justifi-tokenize-payment-method>` API (@justifi/webcomponents 6.13.7)
 * that we drive. Two things about its contract are load-bearing:
 *
 * 1. `tokenizePaymentMethod()` resolves to `undefined` when the component's OWN validation
 *    fails — it reports the reason only by emitting `error-event`, never through the promise.
 *    So an empty resolve means "invalid input", NOT "tokenized but malformed response".
 * 2. When JustiFi rejects the tokenization (bad token, declined card, ...) it resolves to
 *    `{ error }` instead of throwing.
 *
 * Both cases must be surfaced, or pressing Continue looks like it did nothing (RUT-2836).
 */
interface JustifiValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

interface JustifiTokenizedCard {
  token?: string;
  acct_last_four?: string;
  brand?: string;
  month?: string;
  year?: string;
}

interface JustifiTokenizeResponse {
  token?: string;
  data?: {
    card?: JustifiTokenizedCard;
    bank_account?: JustifiTokenizedCard;
  };
  error?: {
    code: string;
    message: string;
  };
}

type JustifiTokenizeElement = HTMLElement & {
  validate: () => Promise<JustifiValidationResult>;
  tokenizePaymentMethod: () => Promise<JustifiTokenizeResponse | undefined>;
  fillBillingForm?: (fields: JustifiBillingPrefill) => Promise<void>;
};

function compactJustifiShadowLayout(element: JustifiTokenizeElement) {
  const root = element.shadowRoot;
  if (!root) return;

  root.querySelectorAll('.mt-4, .pb-4, .mb-3').forEach((node) => {
    if (node instanceof HTMLElement) {
      node.style.marginTop = '0';
      node.style.marginBottom = '0';
      node.style.paddingBottom = '0';
    }
  });
}

async function prefillJustifiBillingForm(
  element: JustifiTokenizeElement,
  billingPrefill?: JustifiBillingPrefill,
) {
  if (!billingPrefill || typeof element.fillBillingForm !== 'function') return;

  const fields: JustifiBillingPrefill = {};
  if (billingPrefill.name) fields.name = billingPrefill.name;
  if (billingPrefill.address_postal_code) {
    fields.address_postal_code = billingPrefill.address_postal_code;
  }

  if (Object.keys(fields).length === 0) return;
  await element.fillBillingForm(fields);
}

interface JustifiPaymentFormProps {
  accountId: string;
  authToken: string;
  /** When true, the Justifi form allows ACH bank accounts in addition to cards. */
  enableAch?: boolean;
  billingPrefill?: JustifiBillingPrefill;
  onSuccess?: (data: JustifiPaymentMethod) => void;
  onError?: (error: string) => void;
}

const JustifiPaymentForm = forwardRef<JustifiPaymentFormHandle, JustifiPaymentFormProps>(
  function JustifiPaymentForm({
    accountId,
    authToken,
    enableAch = false,
    billingPrefill,
    onSuccess,
    onError,
  }, ref) {
    const [isLoaded, setIsLoaded] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [paymentType, setPaymentType] = useState<'card' | 'bank_account'>('card');
    const formRef = useRef<HTMLElement | null>(null);
    // The component reports tokenization/validation failures on `error-event` only.
    const lastErrorMessage = useRef<string | null>(null);

    const showPaymentTypeTabs = enableAch;
    const disableBankAccount = !enableAch || paymentType === 'card';
    const disableCreditCard = enableAch && paymentType === 'bank_account';

    const hideBillingForm =
      paymentType === 'bank_account'
        ? Boolean(billingPrefill?.name)
        : Boolean(billingPrefill?.address_postal_code);

    // Callers build `billingPrefill` inline, so track its values — not its identity —
    // or every parent render would look like a new prefill to the effect below.
    const prefillName = billingPrefill?.name;
    const prefillPostalCode = billingPrefill?.address_postal_code;

    // Wait for the web component to be defined
    useEffect(() => {
      const timeout = setTimeout(() => {
        setLoadError('Payment form failed to load. Please refresh the page and try again.');
      }, 10000);

      customElements.whenDefined('justifi-tokenize-payment-method')
        .then(() => {
          clearTimeout(timeout);
          setIsLoaded(true);
        })
        .catch(() => {
          clearTimeout(timeout);
          setLoadError('Payment form not available. Please refresh the page and try again.');
        });

      return () => clearTimeout(timeout);
    }, []);

    // Capture the reason behind a failed tokenize — it arrives as an event, not on the promise.
    // Re-attach when the element remounts (its `key` changes on a card/bank tab switch).
    useEffect(() => {
      const element = formRef.current;
      if (!element) return;

      const handleErrorEvent = (event: Event) => {
        const { detail } = event as CustomEvent<{ message?: string }>;
        if (detail?.message) {
          lastErrorMessage.current = detail.message;
        }
      };

      element.addEventListener('error-event', handleErrorEvent);
      return () => element.removeEventListener('error-event', handleErrorEvent);
    }, [isLoaded, paymentType]);

    // Tighten the web component's default spacing. Layout only — prefilling from here
    // would re-run on every shadow DOM mutation, including the ones the owner's own
    // typing causes.
    useEffect(() => {
      if (!isLoaded || !formRef.current) return;

      const element = formRef.current as JustifiTokenizeElement;
      const syncLayout = () => compactJustifiShadowLayout(element);

      syncLayout();
      const observer = new MutationObserver(syncLayout);
      if (element.shadowRoot) {
        observer.observe(element.shadowRoot, { childList: true, subtree: true });
      }

      return () => observer.disconnect();
    }, [isLoaded, paymentType]);

    // Prefill the billing details exactly once per element. `fillBillingForm` REPLACES
    // the whole billing form state, so a second call wipes anything typed since the
    // first — including the postal code the owner has to enter for a card. The shadow
    // form renders after we mount, so re-attempt until the first fill lands, then stop.
    useEffect(() => {
      if (!isLoaded || !formRef.current) return;
      if (!prefillName && !prefillPostalCode) return;

      const element = formRef.current as JustifiTokenizeElement;
      const fields = { name: prefillName, address_postal_code: prefillPostalCode };
      let observer: MutationObserver | undefined;
      let filled = false;
      let filling = false;

      const attemptPrefill = () => {
        // Filling before the shadow form exists is a no-op, so wait for it to render.
        if (filled || filling || !element.shadowRoot?.querySelector('input')) return;
        filling = true;
        prefillJustifiBillingForm(element, fields)
          .then(() => {
            filled = true;
            observer?.disconnect();
          })
          .catch(() => {
            // Best effort — the owner can still type the details themselves.
          })
          .finally(() => {
            filling = false;
          });
      };

      attemptPrefill();
      if (element.shadowRoot) {
        observer = new MutationObserver(attemptPrefill);
        observer.observe(element.shadowRoot, { childList: true, subtree: true });
      }

      return () => observer?.disconnect();
    }, [isLoaded, paymentType, prefillName, prefillPostalCode]);

    useImperativeHandle(ref, () => ({
      submit: async () => {
        const element = formRef.current as JustifiTokenizeElement | null;
        if (!element || typeof element.tokenizePaymentMethod !== 'function') {
          throw new Error('Payment form not ready. Please refresh the page and try again.');
        }

        // Surface the error on the form itself, then let the caller react to the throw.
        // The web component stays mounted throughout, so nothing the owner typed is lost.
        const fail: (message: string) => never = (message) => {
          setSubmitError(message);
          onError?.(message);
          throw new Error(message);
        };

        setSubmitError(null);
        lastErrorMessage.current = null;

        // Validate up front: `tokenizePaymentMethod()` throws away the reason a form is
        // invalid, and we need to tell the owner which field to fix.
        const validation = await element.validate();
        if (!validation.isValid) {
          const reasons = Object.values(validation.errors).filter(Boolean);
          fail(reasons.length > 0
            ? reasons.join(' ')
            : 'Check your payment details and try again.');
        }

        const result = await element.tokenizePaymentMethod();

        if (result?.error) {
          fail(result.error.message);
        }

        const paymentMethodId = result?.token;
        if (!paymentMethodId) {
          fail(lastErrorMessage.current ?? 'We could not save that payment method. Please check your details and try again.');
        }

        const tokenized = result?.data?.bank_account ?? result?.data?.card;
        onSuccess?.({
          paymentMethodId,
          type: result?.data?.bank_account ? 'bank_account' : 'card',
          last4: tokenized?.acct_last_four,
          brand: tokenized?.brand,
          expMonth: tokenized?.month,
          expYear: tokenized?.year,
        });
      },
    }), [onSuccess, onError]);

    if (loadError) {
      return (
        <Alert variant="destructive">
          <WarningCircle className="h-4 w-4" />
          <AlertDescription>{loadError}</AlertDescription>
        </Alert>
      );
    }

    if (!isLoaded) {
      return (
        <div className="flex items-center justify-center py-8">
          <CircleNotch className="h-6 w-6 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Loading payment form...</span>
        </div>
      );
    }

    const containerClassName = [
      'justifi-form-container',
      showPaymentTypeTabs && 'justifi-form-container--external-tabs',
      hideBillingForm && 'justifi-form-container--hide-billing',
    ].filter(Boolean).join(' ');

    const justifiElement = (
      <div className={containerClassName}>
        {submitError && (
          <Alert variant="destructive" className="mb-4">
            <WarningCircle className="h-4 w-4" />
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        )}
        <justifi-tokenize-payment-method
          key={showPaymentTypeTabs ? paymentType : 'card'}
          ref={formRef}
          account-id={accountId}
          auth-token={authToken}
          hide-bank-account-billing-form={true}
          hide-card-billing-form={true}
          disable-bank-account={disableBankAccount}
          disable-credit-card={disableCreditCard}
          hide-submit-button={true}
        />
      </div>
    );

    if (!showPaymentTypeTabs) {
      return justifiElement;
    }

    return (
      <Tabs
        value={paymentType}
        onValueChange={(value) => setPaymentType(value as 'card' | 'bank_account')}
      >
        <TabsList className="mb-3 grid w-full grid-cols-2">
          <TabsTrigger value="card" className="text-xs">
            Credit card
          </TabsTrigger>
          <TabsTrigger value="bank_account" className="text-xs">
            Bank account
          </TabsTrigger>
        </TabsList>
        <TabsContent value={paymentType} className="mt-0">
          {justifiElement}
        </TabsContent>
      </Tabs>
    );
  }
);

export default JustifiPaymentForm;
